import { useEffect, useState } from "react";
import type { FieldValues, UseControllerReturn } from "react-hook-form";

import { Input } from "./input";

function formatPhoneNumber(value: string): string {
  const cleaned = value.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11) {
    return `+${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return value;
}

function cleanPhoneNumber(value: string): string {
  return value.replace(/\D/g, "");
}

function PhoneNumberInput<T extends FieldValues>({
  field,
  id,
  className,
  autoFocus,
  placeholder,
}: {
  field: UseControllerReturn<T>["field"];
  id: string;
  className?: string;
  autoFocus?: boolean;
  placeholder?: string;
}) {
  const fieldOnChange = (value: string) => {
    const cleaned = cleanPhoneNumber(value);
    field.onChange(cleaned);
  };

  const [displayValue, setDisplayValue] = useState<string>(
    field.value ? formatPhoneNumber(field.value) : "",
  );

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    fieldOnChange(value);
    setDisplayValue(formatPhoneNumber(value));
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDisplayValue(value);
    fieldOnChange(value);
  };

  return (
    <Input
      type="tel"
      className={className}
      id={id}
      value={displayValue}
      onChange={onChange}
      onBlur={onBlur}
      autoFocus={autoFocus}
      placeholder={placeholder}
    />
  );
}

// Phone number input component that works with refs (for use with EntityField pattern)
function PhoneNumberInputWithRef({
  inputRef,
  defaultValue,
  className,
  autoFocus,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  defaultValue?: string;
  className?: string;
  autoFocus?: boolean;
}) {
  const [displayValue, setDisplayValue] = useState<string>(
    defaultValue ? formatPhoneNumber(defaultValue) : "",
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDisplayValue(value);
    // Store cleaned value in a data attribute so we can read it from the ref
    const cleaned = cleanPhoneNumber(value);
    if (inputRef.current) {
      inputRef.current.setAttribute("data-cleaned-value", cleaned);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cleaned = cleanPhoneNumber(value);
    if (inputRef.current) {
      inputRef.current.setAttribute("data-cleaned-value", cleaned);
    }
    setDisplayValue(formatPhoneNumber(value));
  };

  // Initialize the data attribute when component mounts
  useEffect(() => {
    const initialCleaned = defaultValue ? cleanPhoneNumber(defaultValue) : "";
    if (inputRef.current) {
      inputRef.current.setAttribute("data-cleaned-value", initialCleaned);
    }
  }, [defaultValue, inputRef]);

  return (
    <Input
      ref={inputRef}
      type="tel"
      className={className}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      autoFocus={autoFocus}
    />
  );
}

export { PhoneNumberInput, PhoneNumberInputWithRef };
